import { api, type Project } from "./api.js";
import { config } from "../app/env.js";

/**
 * Suivre un projet en direct : Firestore `projects/{id}` si Firebase est configuré (l'étape
 * change sous les yeux, sans attendre), sinon `GET /v1/projects/{id}` toutes les 3 s.
 * `read` : la lecture par l'API (celle, partagée, du magasin des projets — features/projects/store.ts).
 * Renvoie une fonction pour arrêter.
 */
export function watchProject(
  id: string,
  onProject: (p: Project) => void,
  onError: (e: unknown) => void,
  everyMs = 3000,
  read: () => Promise<Project> = () => api.project(id),
): () => void {
  let stopped = false;
  let revision = 0;
  let unsubscribe: (() => void) | null = null;
  // Le dernier projet rendu par l'API : ses champs calculés (URL signées, vignette) n'existent que là.
  let lastApi: Project | null = null;

  const refresh = async (expected = ++revision) => {
    try {
      const p = await read();
      if (!stopped && revision === expected) {
        lastApi = p;
        onProject(p);
      }
    } catch (e) {
      if (!stopped && revision === expected) onError(e);
    }
  };
  const poll = async () => {
    while (!stopped) {
      await refresh();
      await new Promise((r) => setTimeout(r, everyMs));
    }
  };

  if (config.firebase) {
    // Une première lecture par l'API, tout de suite : les champs calculés (URL signées) n'existent que là,
    // et l'écran n'attend plus le SDK Firestore (~550 Ko) ni sa connexion pour s'afficher.
    void refresh();
    void (async () => {
      try {
        const [{ getApps, initializeApp }, fs] = await Promise.all([import("firebase/app"), import("firebase/firestore")]);
        const app = getApps()[0] ?? initializeApp(config.firebase!);
        if (stopped) return;
        unsubscribe = fs.onSnapshot(
          fs.doc(fs.getFirestore(app), "projects", id),
          (snap) => {
            const expected = ++revision;
            if (stopped || !snap.exists()) return;
            // Firestore n'a que les champs bruts : posés sur le dernier projet de l'API, pour garder ses URL signées.
            const p: Project = { ...(lastApi ?? {}), ...(snap.data() as Omit<Project, "id">), id: snap.id };
            // Firestore contient la cause brute, pas la phrase traduite ni l'action.
            // Un échec doit toujours repasser par la même présentation que la liste.
            if (p.status === "failed") return void refresh(expected);
            // Une étape ou une version de plus : les champs calculés changent (vignette, lecture), l'API les redonne.
            const moved = !lastApi || lastApi.status !== p.status || lastApi.currentVersion !== p.currentVersion;
            onProject(p);
            if (moved) void refresh(expected);
          },
          // Règles Firestore refusées, hors ligne… : on retombe sur l'API.
          () => {
            unsubscribe = null;
            void poll();
          },
        );
      } catch {
        void poll();
      }
    })();
  } else {
    void poll();
  }

  return () => {
    stopped = true;
    unsubscribe?.();
  };
}

/**
 * Attendre qu'un travail (export) se termine, en le relisant toutes les 3 s. Arrêté entre deux
 * lectures, il ne signale plus rien ; une coupure réseau passagère est relue, pas remontée
 * (seules plusieurs échecs d'affilée deviennent une erreur).
 */
export async function waitJob<T extends { status: string }>(read: () => Promise<T>, onUpdate: (j: T) => void, isStopped: () => boolean, everyMs = 3000, maxFailures = 5): Promise<T | null> {
  let failures = 0;
  while (!isStopped()) {
    let j: T;
    try {
      j = await read();
      failures = 0;
    } catch (e) {
      if (isStopped()) return null;
      if (++failures >= maxFailures) throw e;
      await new Promise((r) => setTimeout(r, everyMs));
      continue;
    }
    if (isStopped()) return null;
    onUpdate(j);
    if (j.status === "done" || j.status === "failed") return j;
    await new Promise((r) => setTimeout(r, everyMs));
  }
  return null;
}
